if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
      }

      hide() {
        this.kashiraSetZoom?.(false);
        if (document.fullscreenElement === this) document.exitFullscreen();
        // Hand the page gallery the image the shopper left off on, so the
        // main frame and thumb rail stand where the viewer closed. Modal
        // slides carry bare media ids; the page gallery keys everything on
        // section-prefixed ones.
        const current = this.kashiraIndex && this.kashiraTrack?.children[this.kashiraIndex()];
        const mediaId = current?.dataset?.mediaId;
        const gallery = document.querySelector('media-gallery');
        if (mediaId && gallery?.setActiveMedia) {
          const prefixed = `${gallery.id.replace('MediaGallery-', '')}-${mediaId}`;
          if (gallery.querySelector(`[data-media-id="${prefixed}"]`)) gallery.setActiveMedia(prefixed, false);
        }
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.kashiraInitSlider();
        this.showActiveMedia();
      }

      /* Kashira: the modal is a one-image-at-a-time slider (client call
         2026-07-29 — the Magnific-style viewer): horizontal scroll-snap
         track, edge arrows, "n / total" counter, arrow-key navigation. */
      kashiraInitSlider() {
        if (this.kashiraSliderReady) return;
        this.kashiraSliderReady = true;

        this.kashiraTrack = this.querySelector('[role="document"]');
        this.kashiraCounter = this.querySelector('.product-media-modal__counter');
        const slides = () => [...this.kashiraTrack.children];
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.kashiraGoTo = (index, instant) => {
          if (this.classList.contains('is-zoomed')) this.kashiraSetZoom(false);
          const list = slides();
          const clamped = Math.max(0, Math.min(list.length - 1, index));
          this.kashiraTrack.scrollTo({
            left: clamped * this.kashiraTrack.clientWidth,
            behavior: instant || reduce.matches ? 'auto' : 'smooth',
          });
        };
        this.kashiraIndex = () =>
          Math.max(0, Math.round(this.kashiraTrack.scrollLeft / Math.max(1, this.kashiraTrack.clientWidth)));

        const syncCounter = () => {
          const total = slides().length;
          if (this.kashiraCounter) this.kashiraCounter.textContent = `${this.kashiraIndex() + 1} / ${total}`;
          this.classList.toggle('at-first', this.kashiraIndex() === 0);
          this.classList.toggle('at-last', this.kashiraIndex() >= total - 1);
        };
        let ticking = false;
        this.kashiraTrack.addEventListener(
          'scroll',
          () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => {
              ticking = false;
              syncCounter();
            });
          },
          { passive: true }
        );
        this.kashiraSyncCounter = syncCounter;

        // ModalDialog closes the media modal on ANY mouse pointerup
        // (click-anywhere-to-close); controls must not count as "anywhere".
        const shield = (element) => element?.addEventListener('pointerup', (event) => event.stopPropagation());
        const wireArrow = (selector, step) => {
          const arrow = this.querySelector(selector);
          if (!arrow) return;
          shield(arrow);
          arrow.addEventListener('click', () => this.kashiraGoTo(this.kashiraIndex() + step));
        };
        wireArrow('.product-media-modal__arrow--prev', -1);
        wireArrow('.product-media-modal__arrow--next', 1);
        this.addEventListener('keyup', (event) => {
          if (event.code === 'ArrowLeft') this.kashiraGoTo(this.kashiraIndex() - 1);
          if (event.code === 'ArrowRight') this.kashiraGoTo(this.kashiraIndex() + 1);
        });

        /* Zoom (reference parity): the zoom button or a click on the image
           toggles 2.25x on the current slide; while zoomed the cursor pans
           by walking the transform origin. Navigation resets it. */
        this.kashiraActiveImg = () => {
          const slide = slides()[this.kashiraIndex()];
          return slide?.nodeName === 'IMG' ? slide : slide?.querySelector('img');
        };
        this.kashiraSetZoom = (on, originX, originY) => {
          const img = this.kashiraActiveImg();
          if (!img) return;
          this.classList.toggle('is-zoomed', on);
          if (on) {
            img.style.transformOrigin = `${originX ?? 50}% ${originY ?? 50}%`;
            img.style.transform = 'scale(2.25)';
            this.kashiraZoomedImg = img;
          } else {
            (this.kashiraZoomedImg || img).style.transform = '';
            this.kashiraZoomedImg = null;
          }
        };
        const zoomButton = this.querySelector('.product-media-modal__tool--zoom');
        shield(zoomButton);
        zoomButton?.addEventListener('click', () => this.kashiraSetZoom(!this.classList.contains('is-zoomed')));

        this.kashiraTrack.addEventListener('pointerup', (event) => {
          const img = event.target.closest('img');
          if (!img) return;
          // The image is a zoom surface, not "anywhere" — it must not close.
          event.stopPropagation();
          const rect = img.getBoundingClientRect();
          this.kashiraSetZoom(
            !this.classList.contains('is-zoomed'),
            ((event.clientX - rect.left) / rect.width) * 100,
            ((event.clientY - rect.top) / rect.height) * 100
          );
        });
        this.kashiraTrack.addEventListener('pointermove', (event) => {
          if (!this.classList.contains('is-zoomed') || !this.kashiraZoomedImg) return;
          const rect = this.kashiraTrack.getBoundingClientRect();
          const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
          const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
          this.kashiraZoomedImg.style.transformOrigin = `${x}% ${y}%`;
        });

        /* True fullscreen on black (reference parity). Hidden where the
           Fullscreen API is unavailable (iOS Safari). */
        const fullscreenButton = this.querySelector('.product-media-modal__tool--fullscreen');
        if (fullscreenButton) {
          if (!this.requestFullscreen) {
            fullscreenButton.hidden = true;
          } else {
            shield(fullscreenButton);
            fullscreenButton.addEventListener('click', () => {
              if (document.fullscreenElement) document.exitFullscreen();
              else this.requestFullscreen();
            });
            document.addEventListener('fullscreenchange', () => {
              this.classList.toggle('is-fullscreen', document.fullscreenElement === this);
              // Track geometry changes with the viewport; re-land the slide.
              this.kashiraGoTo(this.kashiraIndex(), true);
            });
          }
        }
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');

        // Land directly on the clicked image, then let scroll events keep
        // the counter honest from here on.
        const index = [...this.kashiraTrack.children].indexOf(activeMedia);
        this.kashiraGoTo(index, true);
        this.kashiraSyncCounter?.();

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
